import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, setRating }) => {
  const [hoverRating, setHoverRating] = useState(0);
  return (
    <div className="flex space-x-1" data-testid="dept-star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          data-testid={`dept-star-${star}`}
          className={`text-3xl transition-colors cursor-pointer hover:scale-110 ${
            star <= (hoverRating || rating) ? 'text-yellow-400' : 'text-gray-400'
          }`}
          onClick={() => setRating(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
        >
          ★
        </button>
      ))}
    </div>
  );
};

const DepartmentFeedback = () => {
  const { departmentId } = useParams();
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    customerName: '',
    mobileNumber: '',
    rating: 0,
  });

  useEffect(() => {
    const fetchDept = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/departments/${departmentId}/public`);
        setDepartment(res.data);
      } catch (err) {
        setError('Department not found or not available for feedback');
      } finally {
        setLoading(false);
      }
    };
    fetchDept();
  }, [departmentId]);

  const validateMobile = (number) => {
    const uaePattern = /^(\+971|00971|971)?[-\s]?[5][0-9][-\s]?[0-9]{3}[-\s]?[0-9]{4}$/;
    return uaePattern.test(number.replace(/\s/g, ''));
  };

  const formatMobile = (number) => {
    const digits = number.replace(/\D/g, '');
    if (digits.length >= 9) {
      if (digits.startsWith('971')) {
        return `+${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 8)}-${digits.slice(8, 12)}`;
      } else if (digits.startsWith('5')) {
        return `+971-${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5, 9)}`;
      }
    }
    return number;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.customerName.trim()) return setError('Please enter your name');
    if (!formData.mobileNumber.trim()) return setError('Please enter your mobile number');
    if (!validateMobile(formData.mobileNumber))
      return setError('Please enter a valid UAE mobile number (e.g., +971-50-123-4567)');
    if (formData.rating === 0) return setError('Please select a rating');

    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/organization-feedback`, {
        organization_id: department.organization.id,
        department_id: department.id,
        customer_name: formData.customerName,
        mobile_number: formData.mobileNumber,
        rating: formData.rating,
      });

      if (formData.rating >= 4) {
        if (department?.organization?.google_review_url) {
          window.location.href = department.organization.google_review_url;
        } else {
          navigate(`/organization-feedback-success/${department.organization.id}`, {
            state: { rating: formData.rating },
          });
        }
      } else {
        navigate(`/organization-feedback-details/${department.organization.id}`, {
          state: {
            feedbackId: response.data.id,
            rating: formData.rating,
            customerName: formData.customerName,
          },
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !department) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Department Not Found</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" data-testid="department-feedback-page">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-white px-6 py-8 text-center border-b border-gray-200">
            {department?.organization?.logo ? (
              <img
                src={department.organization.logo}
                alt="Company Logo"
                className="h-12 object-contain inline-block mx-auto mb-4"
              />
            ) : (
              <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-600 text-xl font-bold">
                  {department?.organization?.name?.charAt(0) || 'D'}
                </span>
              </div>
            )}
            <h1 className="text-lg font-semibold text-gray-900" data-testid="dept-name">
              {department?.name}
            </h1>
            <p className="text-xs text-gray-500 mb-2">{department?.organization?.name}</p>
            <p className="text-gray-900 text-sm mt-4">
              We value your feedback! Please rate your experience with this department.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
              <input
                type="text"
                data-testid="dept-customer-name"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
              <input
                type="tel"
                data-testid="dept-mobile-number"
                value={formData.mobileNumber}
                onChange={(e) => setFormData({ ...formData, mobileNumber: formatMobile(e.target.value) })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+971-50-123-4567"
                required
              />
              <p className="text-xs text-gray-500 mt-1">UAE format: +971-5X-XXX-XXXX</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                How would you rate this department?
              </label>
              <div className="flex flex-col items-center space-y-3">
                <StarRating
                  rating={formData.rating}
                  setRating={(rating) => setFormData({ ...formData, rating })}
                />
                <div className="text-xs text-gray-500 font-medium">1=worst – 5=excellent</div>
                <div className="text-sm text-gray-600">
                  {formData.rating === 0 && 'Please select a rating *'}
                  {formData.rating === 1 && 'Very Poor'}
                  {formData.rating === 2 && 'Poor'}
                  {formData.rating === 3 && 'Fair'}
                  {formData.rating === 4 && 'Good'}
                  {formData.rating === 5 && 'Excellent'}
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              data-testid="dept-submit-btn"
              disabled={submitting}
              className="w-full bg-white text-blue-600 py-3 px-4 rounded-md font-medium border-2 border-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>

          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-xs text-gray-500">Thank you for helping us improve our service!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentFeedback;
